import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "../../../components/Modal/Modal";
import StepBuilder from "./StepBuilder";
import {
  MIN_MESSAGE_LENGTH,
  MIN_OFFSET_DAYS,
  SEQUENCE_GROUPS,
} from "../followUpConstants";
import styles from "./SequenceFormModal.module.css";

const DEFAULT_STEP = { offsetDays: 7, messageTemplate: "" };

/**
 * Modal de criação/edição de cadência. Reusa o `Modal` base (overlay, foco no
 * container, fechar com Esc/overlay) e adiciona cabeçalho/rodapé/lógica.
 *
 * O `triggerType` é herdado do grupo (passado por prop) — NÃO há select de tipo.
 * Ao salvar, envia o array COMPLETO de steps (o backend substitui integralmente)
 * com `stepOrder` recalculado 1..N na ordem atual.
 *
 * Não chama a API: delega ao `onSubmit({ name, triggerType, isDefault, steps })`.
 */
export default function SequenceFormModal({
  isOpen,
  triggerType,
  sequence = null,
  submitting = false,
  onSubmit,
  onClose,
}) {
  const isEditMode = Boolean(sequence);
  const titleId = useId();
  const nameId = useId();
  const nameInputRef = useRef(null);

  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [steps, setSteps] = useState([{ ...DEFAULT_STEP }]);
  const [touched, setTouched] = useState(false);

  // (Re)inicializa o formulário sempre que o modal abre ou a sequência muda.
  useEffect(() => {
    if (!isOpen) return;
    setTouched(false);
    if (sequence) {
      setName(sequence.name ?? "");
      setIsDefault(Boolean(sequence.isDefault));
      const initialSteps =
        Array.isArray(sequence.steps) && sequence.steps.length > 0
          ? sequence.steps
              .slice()
              .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
              .map((s) => ({
                offsetDays: s.offsetDays ?? MIN_OFFSET_DAYS,
                messageTemplate: s.messageTemplate ?? "",
              }))
          : [{ ...DEFAULT_STEP }];
      setSteps(initialSteps);
    } else {
      setName("");
      setIsDefault(false);
      setSteps([{ ...DEFAULT_STEP }]);
    }
  }, [isOpen, sequence]);

  // Foco inicial no campo de nome.
  useEffect(() => {
    if (!isOpen) return undefined;
    const raf = requestAnimationFrame(() => nameInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  const group = useMemo(
    () => SEQUENCE_GROUPS.find((g) => g.triggerType === triggerType),
    [triggerType],
  );

  // ─── Validação ─────────────────────────────────────────────────────────
  const nameError = name.trim().length === 0 ? "Dê um nome à cadência." : "";

  const stepErrors = useMemo(() => {
    const errs = {};
    steps.forEach((s, i) => {
      const days = Number(s.offsetDays);
      if (!Number.isFinite(days) || days < MIN_OFFSET_DAYS) {
        errs[i] = "O dia precisa ser pelo menos 1.";
        return;
      }
      if (s.messageTemplate.trim().length < MIN_MESSAGE_LENGTH) {
        errs[i] = `A mensagem precisa ter pelo menos ${MIN_MESSAGE_LENGTH} caracteres.`;
      }
    });
    return errs;
  }, [steps]);

  const hasStepErrors = Object.keys(stepErrors).length > 0;
  const isValid = !nameError && steps.length >= 1 && !hasStepErrors;

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid || submitting) return;
    const payloadSteps = steps.map((s, i) => ({
      stepOrder: i + 1,
      offsetDays: Number(s.offsetDays),
      messageTemplate: s.messageTemplate.trim(),
    }));
    onSubmit({
      name: name.trim(),
      triggerType,
      isDefault,
      steps: payloadSteps,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="640px" titleId={titleId}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <header className={styles.header}>
          <div>
            <h2 id={titleId} className={styles.title}>
              {isEditMode ? "Editar cadência" : "Nova cadência"}
            </h2>
            {group && (
              <p className={styles.subtitle}>
                <span aria-hidden="true">{group.icon}</span> {group.title}
              </p>
            )}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {/* Nome */}
          <div className={styles.field}>
            <label htmlFor={nameId} className={styles.label}>
              Nome da cadência
            </label>
            <input
              id={nameId}
              ref={nameInputRef}
              type="text"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Reativação 30 dias"
              maxLength={100}
              aria-invalid={touched && Boolean(nameError)}
            />
            {touched && nameError && (
              <p className={styles.error} role="alert">
                {nameError}
              </p>
            )}
          </div>

          {/* Cadência principal */}
          <div className={styles.toggleField}>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className={styles.toggleInput}
              />
              <span className={styles.toggleTrack} aria-hidden="true">
                <span className={styles.toggleThumb} />
              </span>
              <span className={styles.toggleLabel}>
                Usar como cadência principal
              </span>
            </label>
            <p className={styles.toggleHint}>
              A cadência principal é a usada automaticamente para novos contatos.
              Só uma pode ser principal por tipo — definir esta como principal
              desmarca a anterior.
            </p>
          </div>

          {/* Steps */}
          <div className={styles.field}>
            <span className={styles.label}>Mensagens da cadência</span>
            <p className={styles.sectionHint}>
              As mensagens são enviadas na ordem abaixo. Use ↑↓ para reordenar.
            </p>
            <StepBuilder
              steps={steps}
              errors={touched ? stepErrors : {}}
              onChange={setSteps}
            />
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={submitting}
          >
            {submitting
              ? "Salvando..."
              : isEditMode
                ? "Salvar alterações"
                : "Criar cadência"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}

SequenceFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  /** triggerType herdado do grupo (INACTIVE_LEAD | INACTIVE_CUSTOMER). */
  triggerType: PropTypes.string.isRequired,
  /** Sequência em edição. `null` = modo criação. */
  sequence: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    isDefault: PropTypes.bool,
    steps: PropTypes.array,
  }),
  submitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
